import queryString from 'query-string';

import type { ApiGuessedLocation } from '#/api/types/ApiGuessedLocation';
import { geocoderResultToSearchLocation } from '#/components/LocationInput/geocoderResultToSearchLocation';
import { getPlacePredictions } from '#/components/LocationInput/getPlacePredictions';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { getItem } from '#/utils/localStorage';
import { setGMapsError } from '#/zustand-stores/gMapsErrorStore';

function guessedLocationToSearchLocation(
  guessedLocation: ApiGuessedLocation,
): SearchLocation {
  return {
    ...guessedLocation,
    geoType: 'ipAddress',
    boundingBoxN: null,
    boundingBoxS: null,
    boundingBoxW: null,
    boundingBoxE: null,
  };
}

export async function searchGetInitialLocation(
  guessedLocation: ApiGuessedLocation,
  locale: I18nLocale,
  embedSearchWidgetSearchLocation?: SearchLocation,
): Promise<SearchLocation> {
  const queryParams = queryString.parse(window.location.search);
  const locationParam = queryParams.location;

  if (!locationParam) {
    return (
      (getItem('searchLocation') as SearchLocation) ??
      embedSearchWidgetSearchLocation ??
      guessedLocationToSearchLocation(guessedLocation)
    );
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('location');
  window.history.replaceState({}, '', url.toString());

  try {
    const [placesLib, geocodingLib] = await Promise.all([
      google.maps.importLibrary('places') as Promise<google.maps.PlacesLibrary>,
      google.maps.importLibrary(
        'geocoding',
      ) as Promise<google.maps.GeocodingLibrary>,
    ]);

    const geocoder = new geocodingLib.Geocoder();
    const sessionToken = new placesLib.AutocompleteSessionToken();

    const locationQuery = Array.isArray(locationParam)
      ? (locationParam[0] ?? '')
      : locationParam;

    // special handling of zip code passed through location query param
    const addressTypes = /^\d{5}$/.test(locationQuery)
      ? ['postal_code']
      : undefined;
    const predictions = await getPlacePredictions({
      input: locationQuery,
      sessionToken,
      addressTypes,
    });

    if (!predictions || predictions.length === 0) {
      return (
        (getItem('searchLocation') as SearchLocation) ??
        guessedLocationToSearchLocation(guessedLocation)
      );
    }

    return new Promise((resolve) => {
      void geocoder.geocode(
        { placeId: predictions[0].placePrediction?.placeId, language: locale },
        (results) => {
          if (results && results.length > 0) {
            resolve(geocoderResultToSearchLocation(results[0], {}));
          } else {
            resolve(
              (getItem('searchLocation') as SearchLocation) ??
                embedSearchWidgetSearchLocation ??
                guessedLocationToSearchLocation(guessedLocation),
            );
          }
        },
      );
    });
  } catch (error) {
    if (error instanceof Error) {
      setGMapsError(error.message);
    }
    return (
      (getItem('searchLocation') as SearchLocation) ??
      guessedLocationToSearchLocation(guessedLocation)
    );
  }
}
